import { createColumnHelper, type ColumnDef } from '@tanstack/react-table';
import type { ItemWithAllPrices } from '@/api/items/items.types';
import { GtinCell } from './cells/gtin-cell';
import { MekCell } from './cells/mek-cell';
import { ItemPriceCell } from './cells/item-price-cell';
import { ItemBedarfCell } from './cells/item-bedarf-cell';

const col = createColumnHelper<ItemWithAllPrices>();

export interface ItemsColumnsCallbacks {
  onEditSpecs: (item: ItemWithAllPrices) => void;
}

const sortedItemGroups = ['Smartphone', 'Tablet', 'Smartwatch', 'Notebook', 'Zubehör'];

export const buildItemsColumns = (
  cb: ItemsColumnsCallbacks,
): ColumnDef<ItemWithAllPrices, any>[] => [
  col.display({
    id: 'select',
    header: ({ table }) => (
      <input
        type="checkbox"
        className="items-select-checkbox"
        checked={table.getIsAllRowsSelected()}
        ref={(el) => {
          if (el) el.indeterminate = table.getIsSomeRowsSelected();
        }}
        onChange={table.getToggleAllRowsSelectedHandler()}
        onClick={(e) => e.stopPropagation()}
      />
    ),
    size: 28,
    minSize: 28,
    enableSorting: false,
    enableResizing: false,
    cell: ({ row }) => (
      <input
        type="checkbox"
        className="items-select-checkbox"
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect()}
        onChange={row.getToggleSelectedHandler()}
        onClick={(e) => e.stopPropagation()}
      />
    ),
  }),
  col.display({
    id: 'edit-specs',
    header: '',
    size: 32,
    minSize: 32,
    enableSorting: false,
    enableResizing: false,
    cell: ({ row }) => (
      <button
        type="button"
        className="items-edit-specs-btn"
        title="Spezifikationen bearbeiten"
        onClick={(e) => {
          e.stopPropagation();
          cb.onEditSpecs(row.original);
        }}
      >
        ✎
      </button>
    ),
  }),
  ...itemsColumns,
];

export const itemsColumns: ColumnDef<ItemWithAllPrices, any>[] = [
  col.accessor('id', {
    header: 'Art.-Nr.',
    size: 132,
    cell: (info) => (
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--ink-2)' }} title={info.getValue()}>
        {info.getValue()}
      </span>
    ),
  }),
  col.accessor('barCode', {
    header: 'EAN',
    size: 140,
    cell: (info) => <GtinCell value={info.getValue()} />,
  }),
  col.accessor('name', {
    header: 'Bezeichnung',
    size: 400,
    minSize: 200,
    cell: (info) => (
      <span style={{ fontWeight: 500, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={info.getValue()}>
        {info.getValue()}
      </span>
    ),
  }),
  col.accessor('movingAveragePrice', {
    header: 'MEK',
    size: 80,
    minSize: 70,
    meta: { numeric: true },
    cell: (info) => <MekCell value={info.getValue()} />,
  }),
  col.accessor('standardPrice', {
    header: 'Standard',
    size: 130,
    minSize: 100,
    meta: { numeric: true },
    cell: ({ row }) => <ItemPriceCell price={row.original.standardPrice} mek={row.original.movingAveragePrice} />,
  }),
  col.accessor('itscopePrice', {
    header: 'ITscope',
    size: 130,
    minSize: 100,
    meta: { numeric: true },
    cell: ({ row }) => <ItemPriceCell price={row.original.itscopePrice} mek={row.original.movingAveragePrice} />,
  }),
  col.accessor('onlinerPrice', {
    header: 'Onliner',
    size: 130,
    minSize: 100,
    meta: { numeric: true },
    cell: ({ row }) => <ItemPriceCell price={row.original.onlinerPrice} mek={row.original.movingAveragePrice} />,
  }),
  col.accessor('quantityOnStock', {
    header: 'Lager',
    size: 55,
    minSize: 45,
    meta: { numeric: true },
  }),
  col.accessor('quantityOrderedByCustomers', {
    header: 'Bestätigt',
    size: 65,
    minSize: 55,
    meta: { numeric: true },
  }),
  col.accessor('quantityOrderedFromVendors', {
    header: 'Bestellt',
    size: 60,
    minSize: 50,
    meta: { numeric: true },
  }),
  col.accessor('minInventory', {
    header: 'Mindest.',
    size: 65,
    minSize: 55,
    meta: { numeric: true },
  }),
  col.display({
    id: 'bedarf',
    header: 'Bedarf',
    size: 60,
    minSize: 50,
    meta: { numeric: true },
    enableSorting: false,
    cell: ({ row }) => <ItemBedarfCell item={row.original} />,
  }),
  col.accessor('manufacturer', {
    header: 'Hersteller',
    size: 100,
    cell: (info) => <span style={{ color: 'var(--ink-3)' }}>{info.getValue()}</span>,
  }),
  col.accessor('itemGroup', {
    header: 'Gruppe',
    size: 90,
    sortingFn: (rowA, rowB) => {
      const a = rowA.original.itemGroup;
      const b = rowB.original.itemGroup;
      const aIdx = a ? sortedItemGroups.indexOf(a) : -1;
      const bIdx = b ? sortedItemGroups.indexOf(b) : -1;
      if (aIdx === -1 && bIdx === -1) return (a ?? '').localeCompare(b ?? '');
      if (aIdx === -1) return 1;
      if (bIdx === -1) return -1;
      return aIdx - bIdx;
    },
    cell: (info) => <span style={{ color: 'var(--ink-3)' }}>{info.getValue()}</span>,
  }),
  col.accessor((row) => row.identity?.productLine.name ?? '', {
    id: 'productLine',
    header: 'Produktlinie',
    size: 120,
    cell: ({ row }) => {
      const v = row.original.identity?.productLine.name;
      return v ? (
        <span style={{ color: 'var(--ink-2)' }} title={v}>
          {v}
        </span>
      ) : (
        <span style={{ color: 'var(--ink-4)' }}>—</span>
      );
    },
  }),
  col.accessor((row) => row.identity?.model.name ?? '', {
    id: 'model',
    header: 'Modell',
    size: 130,
    cell: ({ row }) => {
      const v = row.original.identity?.model.name;
      return v ? (
        <span style={{ color: 'var(--ink-2)' }} title={v}>
          {v}
        </span>
      ) : (
        <span style={{ color: 'var(--ink-4)' }}>—</span>
      );
    },
  }),
  col.accessor((row) => (row.specifications?.storage as string) ?? '', {
    id: 'storage',
    header: 'Speicher',
    size: 80,
    cell: (info) =>
      info.getValue() ? (
        <span style={{ fontFamily: 'var(--font-mono)' }}>{info.getValue()}</span>
      ) : (
        <span style={{ color: 'var(--ink-4)' }}>—</span>
      ),
  }),
  col.accessor(
    (row) =>
      (row.specifications?.color as { value: string } | null | undefined)
        ?.value ?? '',
    {
      id: 'color',
      header: 'Farbe',
      size: 100,
      cell: (info) =>
        info.getValue() ? (
          <span style={{ color: 'var(--ink-2)' }}>{info.getValue()}</span>
        ) : (
          <span style={{ color: 'var(--ink-4)' }}>—</span>
        ),
    },
  ),
];
